import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageCircle, Crown, Lock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MatchesList = ({ user }) => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const isPremium = user?.user?.subscription_status === 'premium';

  const fetchMatches = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/match/list`, {
        withCredentials: true
      });
      setMatches(response.data.matches);
      setLoading(false);
    } catch (error) {
      console.error('Fetch matches error:', error);
      toast.error('Failed to load matches');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMatches();
  }, [fetchMatches]);

  const handleChatClick = (matchId) => {
    if (!isPremium) {
      toast.error('Premium subscription required for messaging');
      navigate('/upgrade');
      return;
    }
    navigate(`/chat/${matchId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading matches...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
      <header className="glass border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Dashboard</span>
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Your Matches</h2>
          <p className="text-gray-600">Connect with people who liked your voice</p>
        </div>

        {!isPremium && matches.length > 0 && (
          <div className="card mb-6 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-200">
            <div className="flex items-start space-x-3">
              <Crown className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Upgrade to Premium</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Unlock messaging and see profile photos of your matches
                </p>
                <button
                  onClick={() => navigate('/upgrade')}
                  className="btn-primary text-sm py-2 px-4"
                  data-testid="upgrade-prompt-btn"
                >
                  Go Premium Now
                </button>
              </div>
            </div>
          </div>
        )}

        {matches.length === 0 ? (
          <div className="card text-center py-12">
            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No matches yet</h3>
            <p className="text-gray-600 mb-4">Start discovering profiles to find your matches</p>
            <button
              onClick={() => navigate('/discover')}
              className="btn-primary"
              data-testid="discover-btn"
            >
              Discover Profiles
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {matches.map((match) => (
              <div
                key={match.match_id}
                className="card hover:shadow-xl transition-all cursor-pointer"
                onClick={() => handleChatClick(match.match_id)}
                data-testid={`match-card-${match.match_id}`}
              >
                <div className="flex items-center space-x-4">
                  {/* Avatar */}
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white text-2xl font-bold">
                      {match.user.name[0].toUpperCase()}
                    </div>
                    {match.user.profile_photo && isPremium && (
                      <img
                        src={match.user.profile_photo}
                        alt={match.user.name}
                        className="absolute inset-0 w-16 h-16 rounded-full object-cover"
                      />
                    )}
                    {!isPremium && (
                      <div className="absolute inset-0 w-16 h-16 rounded-full bg-black bg-opacity-50 flex items-center justify-center">
                        <Lock className="w-6 h-6 text-white" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900">{match.user.name}</h3>
                    <p className="text-sm text-gray-500">
                      Matched {new Date(match.created_at).toLocaleDateString()}
                    </p>
                  </div>

                  {/* Action */}
                  <div>
                    {isPremium ? (
                      <div className="flex items-center space-x-2 text-pink-500">
                        <MessageCircle className="w-5 h-5" />
                        <span className="font-semibold">Chat</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2 text-gray-400">
                        <Lock className="w-5 h-5" />
                        <span className="text-sm">Premium</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default MatchesList;